import { useState, useEffect } from "react";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Badge from "@/components/ui/badge/Badge";
import { Modal } from "@/components/ui/modal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { userService } from "@/services/userService";
import { handleApiError } from "@/services/api";
import type { User } from "@/types/models";
import { useIsOwner } from "@/hooks/useRole";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

const roleColors: Record<string, "primary" | "success" | "warning" | "info" | "dark"> = {
  admin: "primary",
  owner: "success",
  manager: "dark",
  supervisor: "warning",
  viewer: "info",
};

export default function UsersPage() {
  const isOwner = useIsOwner();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Create/Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"owner" | "manager" | "supervisor" | "viewer">("supervisor");
  const [pin, setPin] = useState("");
  const [active, setActive] = useState(true);

  // Reset PIN modal
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinTarget, setPinTarget] = useState<User | null>(null);
  const [newPin, setNewPin] = useState("");
  const [pinSaving, setPinSaving] = useState(false);

  const loadUsers = async () => {
    setError("");
    try {
      const res = await userService.list();
      if (Array.isArray(res)) {
        setUsers(res as User[]);
      } else {
        const paginated = res as unknown as { data: User[] };
        setUsers(paginated.data ?? []);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setPhone("");
    setEmail("");
    setRole("supervisor");
    setPin("");
    setActive(true);
    setError("");
    setModalOpen(true);
  };

  const openEdit = (user: User) => {
    setEditing(user);
    setName(user.name);
    setPhone(user.phone);
    setEmail(user.email ?? "");
    setRole(user.role === "admin" ? "owner" : (user.role as "owner" | "manager" | "supervisor" | "viewer"));
    setActive(user.active);
    setPin("");
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      if (editing) {
        await userService.update(editing.id, { phone, name, role, active, email: email || undefined });
      } else {
        await userService.create({ phone, name, pin, role, email: email || undefined });
      }
      setModalOpen(false);
      setLoading(true);
      await loadUsers();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openResetPIN = (user: User) => {
    setPinTarget(user);
    setNewPin("");
    setError("");
    setPinModalOpen(true);
  };

  const handleResetPIN = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pinTarget) return;
    setPinSaving(true);
    setError("");

    try {
      await userService.resetPIN(pinTarget.id, newPin);
      setPinModalOpen(false);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setPinSaving(false);
    }
  };

  if (!isOwner) {
    return (
      <>
        <PageMeta title="Users | Poultry Book" description="Manage farm users" />
        <PageBreadcrumb pageTitle="Users" />
        <div className="card p-10 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            You have view-only access. User management requires an admin or owner role.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageMeta title="Users | Poultry Book" description="Manage farm users" />
      <PageBreadcrumb pageTitle="Users" />

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Farm Users
          </h3>
          {isOwner && (
            <Button size="sm" onClick={openCreate}>
              Add User
            </Button>
          )}
        </div>

        {error && !modalOpen && !pinModalOpen && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : users.length === 0 && !error ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              No users found. Add your first user.
            </div>
          ) : users.length === 0 ? null : (
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Name
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Phone
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Email
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Role
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Status
                  </TableCell>
                  {isOwner && (
                    <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="border-t border-gray-100 dark:border-gray-800">
                    <TableCell className="px-5 py-3 text-sm font-medium text-gray-800 dark:text-white/90">
                      {user.name}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {user.phone}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {user.email || "—"}
                    </TableCell>
                    <TableCell className="px-5 py-3">
                      <Badge size="sm" color={roleColors[user.role] ?? "light"}>
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-5 py-3">
                      <Badge size="sm" color={user.active ? "success" : "error"}>
                        {user.active ? "active" : "inactive"}
                      </Badge>
                    </TableCell>
                    {isOwner && (
                      <TableCell className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => openEdit(user)}
                            className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => openResetPIN(user)}
                            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          >
                            Reset PIN
                          </button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-md p-5 sm:p-8 mx-4">
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editing ? "Edit User" : "Add User"}
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="userName">Name *</Label>
            <Input
              id="userName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Raju Kumar"
              required
              maxLength={100}
            />
          </div>

          <div>
            <Label htmlFor="userPhone">Phone *</Label>
            <Input
              id="userPhone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 9876543210"
              required
              minLength={10}
              maxLength={15}
            />
          </div>

          <div>
            <Label htmlFor="userEmail">Email (for OTP verification)</Label>
            <Input
              id="userEmail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. user@example.com"
              maxLength={255}
            />
          </div>

          <div>
            <Label htmlFor="userRole">Role *</Label>
            <select
              id="userRole"
              value={role}
              onChange={(e) => setRole(e.target.value as "owner" | "manager" | "supervisor" | "viewer")}
              className={selectClass}
            >
              <option value="supervisor">Supervisor</option>
              <option value="manager">Manager</option>
              <option value="owner">Owner</option>
              <option value="viewer">Viewer (read-only)</option>
            </select>
          </div>

          {!editing && (
            <div>
              <Label htmlFor="userPin">PIN * (6 digits)</Label>
              <Input
                id="userPin"
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit PIN"
                required
                minLength={6}
                maxLength={6}
              />
            </div>
          )}

          {editing && (
            <div className="flex items-center gap-3">
              <input
                id="userActive"
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
              />
              <Label htmlFor="userActive">Active</Label>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !name || !phone || (!editing && pin.length !== 6)}>
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Reset PIN Modal */}
      <Modal isOpen={pinModalOpen} onClose={() => setPinModalOpen(false)} className="max-w-sm p-5 sm:p-8 mx-4">
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          Reset PIN for {pinTarget?.name}
        </h4>

        {error && pinModalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleResetPIN} className="space-y-4">
          <div>
            <Label htmlFor="newPin">New PIN * (6 digits)</Label>
            <Input
              id="newPin"
              type="password"
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit PIN"
              required
              minLength={6}
              maxLength={6}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" onClick={() => setPinModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={pinSaving || newPin.length !== 6}>
              {pinSaving ? "Resetting..." : "Reset PIN"}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
